import { useEffect, useState, memo } from "react";
import { Box, Grid, Skeleton, Typography, useTheme } from "@mui/material";
import {
    CameraSquare,
    Banknote2,
    Dollar,
    Card as CardIcon,
    SaleSquare,
    Wallet,
    GraphUp,
} from "@solar-icons/react/ssr";

import { request } from "@/Api";
import { setError } from "@/nav/navSlice";
import { useDispatch } from "react-redux";

const FinancialSnapshot = memo(() => {
    const theme = useTheme();
    const [reports, setReports] = useState(null);
    const dispatch = useDispatch();

    useEffect(() => {
        request
            .get("/reports", {
                params: { group_type: "country", period_type: "month" },
            })
            .then((response) => {
                setReports(response.data);
            })
            .catch((err) => {
                setReports(null);
                dispatch(
                    setError({
                        message:
                            err?.response?.data?.detail ||
                            "Failed to load financial report",
                    }),
                );
            });
    }, [dispatch]);

    const cardSx = {
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.mode === "dark" ? "#363636" : theme.palette.divider}`,
        borderRadius: "14px",
        padding: "14px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        height: "100%",
        boxSizing: "border-box",
    };

    const labelSx = {
        color: "text.secondary",
        fontSize: 12,
        fontFamily: "Inter Tight, sans-serif",
        fontWeight: 400,
        lineHeight: 1,
        whiteSpace: "nowrap",
    };

    const valueSx = {
        color: "text.primary",
        fontSize: 16,
        fontFamily: "Inter Tight, sans-serif",
        fontWeight: 600,
        lineHeight: 1,
    };

    const subValueSx = {
        color: "text.primary",
        fontSize: 14,
        fontFamily: "Inter Tight, sans-serif",
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: "nowrap",
    };

    if (!reports) {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Skeleton
                    variant="rounded"
                    height={190}
                    sx={{ borderRadius: "14px" }}
                />
                <Grid container spacing={2.5}>
                    {[1, 2, 3, 4].map((i) => (
                        <Grid item xs={6} md={3} key={i}>
                            <Skeleton
                                variant="rounded"
                                height={107}
                                sx={{ borderRadius: "14px" }}
                            />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    const gt = reports.grand_totals;
    const bidRevenue = parseFloat(gt.bid_revenue_total[0]) || 0;
    const prevRevenue = parseFloat(gt.bid_revenue_total[1]) || 0;
    const scheduledBills = parseFloat(gt.scheduled_bills_total[0]) || 0;
    const marginPct = parseFloat(gt.overall_margin[0]) || 0;
    const markupPct = parseFloat(gt.overall_markup[0]) || 0;

    // Pipeline segments
    const inProduction = bidRevenue;
    const pitching = scheduledBills;
    const waiting = bidRevenue * (marginPct / 100);
    const onHold = bidRevenue * (markupPct / 100);
    const pipelineTotal = inProduction + pitching + waiting + onHold;
    const pctOf = (val) =>
        pipelineTotal > 0 ? (val / pipelineTotal) * 100 : 0;

    const revenueChangePct =
        prevRevenue > 0 ? ((bidRevenue - prevRevenue) / prevRevenue) * 100 : 0;
    const hardCostsPct =
        bidRevenue > 0 ? (scheduledBills / bidRevenue) * 100 : 0;
    const grossMarginAmount = bidRevenue * (marginPct / 100);
    const avgSpend = bidRevenue * (markupPct / 100);

    const fmt = (n) =>
        parseFloat(n).toLocaleString("en-CA", { maximumFractionDigits: 0 });

    const pipelineSegments = [
        {
            color: "#21a468",
            barColor: "#1a754c",
            label: "In Production",
            value: inProduction,
            rounded: "left",
        },
        {
            color: "#0082e4",
            barColor: "#015a9d",
            label: "Pitching",
            value: pitching,
            rounded: "none",
        },
        {
            color: theme.palette.text.secondary,
            barColor: "text.secondary",
            label: "Waiting",
            value: waiting,
            rounded: "none",
        },
        {
            color: "#ffac30",
            barColor: "#d08d28",
            label: "On Hold",
            value: onHold,
            rounded: "right",
        },
    ];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Section header — plain label row, not a card */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CameraSquare
                    size={18}
                    color={theme.palette.text.secondary}
                    weight="Outline"
                />
                <Typography sx={{ ...valueSx, fontSize: 16 }}>
                    Key Metrics
                </Typography>
            </Box>

            {/* Pipeline card */}
            <Box sx={cardSx}>
                <Box sx={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <Banknote2
                        size={14}
                        color={theme.palette.text.secondary}
                        weight="Outline"
                    />
                    <Typography sx={labelSx}>Pipeline</Typography>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        paddingTop: "10px",
                    }}
                >
                    <Typography sx={{ ...valueSx, color: "text.secondary" }}>
                        CAD
                    </Typography>
                    <Typography sx={valueSx}>{fmt(pipelineTotal)}</Typography>
                </Box>

                {/* Stacked bar — rounded via container overflow:hidden */}
                <Box sx={{ paddingY: "10px" }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            overflow: "hidden",
                            borderRadius: "8px",
                        }}
                    >
                        {pipelineSegments.map(({ barColor, label, value }) => (
                            <Box
                                key={label}
                                sx={{
                                    width: `${pctOf(value)}%`,
                                    height: 16,
                                    background: barColor,
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Legend */}
                <Box
                    sx={{
                        display: "flex",
                        gap: "38px",
                        alignItems: "flex-start",
                        paddingBottom: "10px",
                        flexWrap: "wrap",
                    }}
                >
                    {pipelineSegments.map(({ color, label, value }) => (
                        <Box
                            key={label}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                                minWidth: 80,
                            }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "5px",
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 10,
                                        height: 10,
                                        borderRadius: "2px",
                                        background: color,
                                        flexShrink: 0,
                                    }}
                                />
                                <Typography sx={labelSx}>{label}</Typography>
                            </Box>
                            <Typography sx={subValueSx}>
                                ${fmt(value)}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>

            {/* 4 metric cards */}
            <Grid container spacing={2.5}>
                <Grid item xs={6} md={3} sx={{ display: "flex" }}>
                    <Box sx={{ ...cardSx, width: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >
                            <Dollar
                                size={14}
                                color={theme.palette.text.secondary}
                                weight="Outline"
                            />
                            <Typography sx={labelSx}>Avg. Revenue</Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                paddingTop: "10px",
                            }}
                        >
                            <Typography
                                sx={{ ...valueSx, color: "text.secondary" }}
                            >
                                CAD
                            </Typography>
                            <Typography sx={valueSx}>
                                {fmt(bidRevenue)}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >
                            <GraphUp
                                size={14}
                                color="#27c77e"
                                weight="Outline"
                            />
                            <Typography sx={{ ...labelSx, color: "#27c77e" }}>
                                {Math.abs(revenueChangePct).toFixed(1)}%
                            </Typography>
                            <Typography sx={labelSx}>vs. plan</Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={6} md={3} sx={{ display: "flex" }}>
                    <Box sx={{ ...cardSx, width: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >
                            <CardIcon
                                size={14}
                                color={theme.palette.text.secondary}
                                weight="Outline"
                            />
                            <Typography sx={labelSx}>
                                Avg. Hard Costs
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                paddingTop: "10px",
                            }}
                        >
                            <Typography
                                sx={{ ...valueSx, color: "text.secondary" }}
                            >
                                CAD
                            </Typography>
                            <Typography sx={valueSx}>
                                {fmt(scheduledBills)}
                            </Typography>
                        </Box>
                        <Typography sx={labelSx}>
                            {hardCostsPct.toFixed(0)}% of revenue
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={6} md={3} sx={{ display: "flex" }}>
                    <Box sx={{ ...cardSx, width: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >
                            <SaleSquare
                                size={14}
                                color={theme.palette.text.secondary}
                                weight="Outline"
                            />
                            <Typography sx={labelSx}>
                                Avg. Gross Margin
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                paddingTop: "10px",
                            }}
                        >
                            <Typography
                                sx={{ ...valueSx, color: "text.secondary" }}
                            >
                                CAD
                            </Typography>
                            <Typography sx={valueSx}>
                                {fmt(grossMarginAmount)}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >
                            <GraphUp
                                size={14}
                                color="#27c77e"
                                weight="Outline"
                            />
                            <Typography sx={{ ...labelSx, color: "#27c77e" }}>
                                {marginPct.toFixed(1)}%
                            </Typography>
                            <Typography sx={labelSx}>margin rate</Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={6} md={3} sx={{ display: "flex" }}>
                    <Box sx={{ ...cardSx, width: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "5px",
                            }}
                        >
                            <Wallet
                                size={14}
                                color={theme.palette.text.secondary}
                                weight="Outline"
                            />
                            <Typography sx={labelSx}>Avg. Spend</Typography>
                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "6px",
                                paddingTop: "10px",
                            }}
                        >
                            <Typography
                                sx={{ ...valueSx, color: "text.secondary" }}
                            >
                                CAD
                            </Typography>
                            <Typography sx={valueSx}>
                                {fmt(avgSpend)}
                            </Typography>
                        </Box>
                        <Typography sx={labelSx}>
                            {markupPct.toFixed(1)}% of revenue
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
});

FinancialSnapshot.displayName = "FinancialSnapshot";

export default FinancialSnapshot;
